import { OmniPickerRow } from "@/components/artifacts/omni/OmniPickerRow.tsx";
import type { SlackPickerChannel } from "@/types/slackChannels.ts";
import { Hash, Lock } from "lucide-react";

type Props = {
  publicChannels: SlackPickerChannel[];
  privateChannels: SlackPickerChannel[];
  isSelected: (channel: SlackPickerChannel) => boolean;
  onToggle: (channelId: string) => void;
  disabled?: boolean;
};

/**
 * The user's Slack channels, public then private, each a checkbox row. A
 * channel okthx is already in is shown checked and locked: there is nothing to
 * add, and taking okthx out is a Slack action, not this card's.
 */
export const SlackChannelChecklist = ({
  publicChannels,
  privateChannels,
  isSelected,
  onToggle,
  disabled,
}: Props) => (
  <>
    <ChannelSection
      title="Public channels"
      channels={publicChannels}
      isSelected={isSelected}
      onToggle={onToggle}
      disabled={disabled}
    />
    <ChannelSection
      title="Private channels"
      channels={privateChannels}
      isSelected={isSelected}
      onToggle={onToggle}
      disabled={disabled}
    />
  </>
);

/** One section's rows under a sticky heading; nothing at all for an empty section, rather than a heading over no rows. */
const ChannelSection = ({
  title,
  channels,
  isSelected,
  onToggle,
  disabled,
}: Omit<Props, "publicChannels" | "privateChannels"> & {
  title: string;
  channels: SlackPickerChannel[];
}) => {
  if (channels.length === 0) return null;
  return (
    <div>
      {/* Sticky so a long list keeps its section in view while it scrolls. */}
      <div className="sticky top-0 z-10 border-b bg-muted px-3 py-1.5 text-xs text-muted-foreground">
        {title}
      </div>
      {channels.map((channel) => {
        const qualifier = channelQualifier(channel);
        return (
          <OmniPickerRow
            key={channel.id}
            checked={isSelected(channel)}
            onToggle={() => onToggle(channel.id)}
            disabled={disabled}
            locked={channel.okthxIsMember}
            leading={
              channel.isPrivate ? (
                <Lock className="size-3.5 text-muted-foreground" />
              ) : (
                <Hash className="size-3.5 text-muted-foreground" />
              )
            }
            label={channel.name}
            qualifier={qualifier}
            ariaState={qualifier}
            statsigName="slack-channel-picker-row"
          />
        );
      })}
    </div>
  );
};

function channelQualifier(channel: SlackPickerChannel): string | undefined {
  if (channel.okthxIsMember) return "okthx is here";
  if (channel.isExternal) return "External";
  return undefined;
}
